import { VideoQueryType } from "../services/api/video"
import { Box, styled, Tooltip, Typography, useTheme } from "@mui/material"
import React from "react"
import { StylelessLink } from "./Util"
import AspectRatioBox from "./AspectRatioBox"
import { HoverOverlay, Overlay } from "./StyledElements"
import { PlaylistType } from "../services/api/playlist"
import { useVideoAPI } from "../services/api/hooks/useVideoAPI"
import { VideoThumbnailImage } from "./VideoThumbnail"
import { PlaylistContextMenu } from "./PlaylistContextMenu"
import { useAuthContext } from "../services/auth"

import { ReactComponent as MenuIcon } from "../assets/svg/menu-dots.svg"
import { ReactComponent as FeatherIcon } from "../assets/svg/feather.svg"
import { ReactComponent as StarIcon } from "../assets/svg/star.svg"

export const PlaylistThumbnail = ({
  playlist,
  hideFeaturedIcon = false,
}: {
  playlist: PlaylistType
  hideFeaturedIcon?: boolean
}): React.ReactElement => {
  const { videosData } = useVideoAPI({ constraint: VideoQueryType.PLAYLIST, code: playlist.code })
  const theme = useTheme()
  const { userId } = useAuthContext()
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number
    mouseY: number
  } | null>(null)
  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault()
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    )
  }
  const handleClose = () => {
    setContextMenu(null)
  }

  return (
    <Box mb={2} onContextMenu={handleContextMenu}>
      <StylelessLink to={videosData.length > 0 ? `/video?v=${videosData[0].id}&pl=${playlist.code}` : "#"}>
        <HoverOverlay sx={{ display: "flex", flexDirection: "column", height: "fit-content" }}>
          <AspectRatioBox ratio={16 / 9}>
            <PlaylistThumbnailLayer z={2} className="playlistSpreadTop">
              {videosData.length > 0 ? (
                <VideoThumbnailImage video={videosData[0]} adaptiveBorder={false} />
              ) : (
                <EmptyPane sx={{ opacity: 0.6 }} />
              )}
              <Overlay className="overlay" borderRadius="10px" />
            </PlaylistThumbnailLayer>
            <PlaylistThumbnailLayer z={1} className="playlistSpreadMiddle">
              {videosData.length > 1 ? (
                <VideoThumbnailImage video={videosData[1]} opacity={0.6} adaptiveBorder={false} />
              ) : (
                <EmptyPane sx={{ opacity: 0.3 }} />
              )}
              <Overlay className="overlay" borderRadius="10px" />
            </PlaylistThumbnailLayer>
            <PlaylistThumbnailLayer z={0} className="playlistSpreadBottom">
              {videosData.length > 2 ? (
                <VideoThumbnailImage video={videosData[2]} opacity={0.2} adaptiveBorder={false} />
              ) : (
                <EmptyPane sx={{ opacity: 0.1 }} />
              )}
              <Overlay className="overlay" borderRadius="10px" />
            </PlaylistThumbnailLayer>
          </AspectRatioBox>
          <Box
            mt={1}
            sx={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ overflow: "hidden", minWidth: 0 }}>
              <Typography marginLeft={1} noWrap variant="h5" fontSize={"1.2rem"} textOverflow="ellipsis">
                {playlist.title}
                {playlist.authorId === userId && (
                  <Box ml={1} component={"span"} display="inline">
                    <Tooltip title={"Créée par vous"}>
                      <FeatherIcon
                        width={"auto"}
                        height={"1rem"}
                        fill={theme.palette.mode === "dark" ? "white" : "black"}
                      />
                    </Tooltip>
                  </Box>
                )}
                {playlist.isPublic && !hideFeaturedIcon && (
                  <Box ml={1} component={"span"} display="inline">
                    <Tooltip title={"Featurée"}>
                      <StarIcon width={"auto"} height={"1rem"} />
                    </Tooltip>
                  </Box>
                )}
              </Typography>
              <Typography
                marginLeft={1}
                noWrap
                variant="subtitle1"
                fontSize={".8rem"}
                color={"gray"}
                textOverflow="ellipsis"
              >
                {videosData.length + " vidéo" + (videosData.length > 1 ? "s" : "")}
              </Typography>
            </Box>
            <Box
              width={"30px"}
              height={"30px"}
              padding={"5px"}
              sx={{
                borderRadius: "50%",
                bgcolor: "transparent",
                "&:hover": { bgcolor: "grey" },
                transition: ".2s",
              }}
            >
              <MenuIcon
                width={"100%"}
                height={"100%"}
                stroke={theme.palette.mode === "dark" ? "white" : "black"}
                style={{ cursor: "pointer" }}
                onClick={handleContextMenu}
              />
            </Box>
          </Box>
        </HoverOverlay>
      </StylelessLink>
      <PlaylistContextMenu
        playlist={playlist}
        open={contextMenu !== null}
        onClose={handleClose}
        position={contextMenu ? { x: contextMenu.mouseX, y: contextMenu.mouseY } : null}
      />
    </Box>
  )
}

const PlaylistThumbnailLayer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "z",
})<{ z?: number }>(({ z = 0 }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "90%",
  height: "90%",
  zIndex: z,
  transition: ".3s",
}))

const EmptyPane = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "white" : "black",
  width: "100%",
  height: "100%",
  borderRadius: "10px",
}))
